import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { KanbanBoard } from "@/components/KanbanBoard";
import { SwimlaneBoard, type LaneBy } from "@/components/SwimlaneBoard";
import { TaskForm } from "@/components/TaskForm";
import { Icon } from "@/components/Icon";
import { Button } from "@/components/Button";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import { Skeleton } from "@/components/Skeleton";

/**
 * S4 Project board — designs/Board.html
 *
 * Layout (top-to-bottom): toolbar (view toggle, group-by, filter, search,
 * add-task) + KanbanBoard. The breadcrumb, ProjectHeader and tab strip live
 * in the parent layout route (`route.tsx`) so they don't flash between tab
 * navigations.
 *
 * The board is intentionally full-bleed: it fills the entire available area
 * of `.proj-shell-body`, columns expand vertically so "Add task" sticks to
 * the column floor, and horizontal overflow scrolls inside `.board-scroll`.
 */
function Board() {
  const { projectId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Shared search: `?q=` is kept in the URL so it survives reload and carries
  // to the List view on switch (List writes it back the same way).
  const { q } = Route.useSearch();

  const [showNew, setShowNew] = useState(false);
  const [view] = useState<"board" | "list">("board");
  const [groupBy, setGroupBy] = useState<"status" | LaneBy>("status");
  const [filterOpen, setFilterOpen] = useState(false);
  const [search, setSearch] = useState(q ?? "");
  const updateSearch = (v: string) => {
    setSearch(v);
    navigate({
      to: "/projects/$projectId",
      params: { projectId },
      search: { q: v || undefined },
      replace: true,
    });
  };
  const [teamFilter, setTeamFilter] = useState("");

  const tasks = useQuery({
    queryKey: queryKeys.tasksFor(projectId),
    queryFn: () => api.listTasks(projectId),
  });
  const team = useQuery({
    queryKey: queryKeys.team(),
    queryFn: () => api.listTeam(),
  });
  const teamsQ = useQuery({ queryKey: ["teams"], queryFn: () => api.teams() });
  const epics = useQuery({
    queryKey: queryKeys.epicsFor(projectId),
    queryFn: () => api.listEpics(projectId),
  });
  const milestonesQ = useQuery({
    queryKey: queryKeys.milestonesFor(projectId),
    queryFn: () => api.listMilestones(projectId),
  });

  if (tasks.isLoading) {
    return (
      <div style={{ padding: 30 }}>
        <Skeleton count={6} height={120} />
      </div>
    );
  }
  if (tasks.isError) {
    return (
      <div style={{ padding: 30 }}>
        Couldn&rsquo;t load tasks for this project.
      </div>
    );
  }

  const allTasks = tasks.data?.tasks ?? [];

  const usersMap: Record<string, { name: string; image?: string | null }> = {};
  for (const m of team.data?.members ?? []) {
    usersMap[m.id] = { name: m.name ?? m.id, image: m.image ?? null };
  }

  // Members of the selected team (for the team filter).
  const teamMemberIds = teamFilter
    ? new Set(
        (
          teamsQ.data?.teams.find((t) => t.id === teamFilter)?.members ?? []
        ).map((m) => m.userId),
      )
    : null;

  const filteredTasks = allTasks.filter((t) => {
    if (search.trim() && !t.title.toLowerCase().includes(search.toLowerCase()))
      return false;
    if (teamMemberIds) {
      const ids = t.assigneeIds?.length
        ? t.assigneeIds
        : t.assigneeId
          ? [t.assigneeId]
          : [];
      if (!ids.some((id) => teamMemberIds.has(id))) return false;
    }
    return true;
  });

  return (
    <div className="proj-board-page">
      <div className="board-bar">
        <SegmentedToggle
          options={[
            { value: "board", label: "Board", icon: "board" },
            { value: "list", label: "List", icon: "clip" },
            { value: "table", label: "Table", icon: "grid" },
            { value: "calendar", label: "Calendar", icon: "cal" },
            { value: "timeline", label: "Timeline", icon: "arrow" },
            { value: "gantt", label: "Gantt", icon: "chev" },
          ]}
          value={view}
          onChange={(v) =>
            navigate({
              to:
                v === "list"
                  ? "/projects/$projectId/list"
                  : v === "table"
                    ? "/projects/$projectId/table"
                    : v === "calendar"
                      ? "/projects/$projectId/calendar"
                      : v === "timeline"
                        ? "/projects/$projectId/timeline"
                        : v === "gantt"
                          ? "/projects/$projectId/gantt"
                          : "/projects/$projectId",
              params: { projectId },
              search: v === "list" ? { q: search || undefined } : undefined,
            })
          }
        />
        <label
          className={`chip-btn group-chip${groupBy !== "status" ? " on" : ""}`}
        >
          <Icon name="board" size={14} />
          <span className="t-sm">Group</span>
          <select
            className="group-select"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as "status" | LaneBy)}
            aria-label="Group board by"
          >
            <option value="status">Status</option>
            <option value="assignee">Assignee (swimlanes)</option>
            <option value="priority">Priority (swimlanes)</option>
            <option value="milestone">Milestone (swimlanes)</option>
            <option value="type">Type (swimlanes)</option>
            <option value="epic">Epic (swimlanes)</option>
          </select>
        </label>
        <button
          type="button"
          className={`chip-btn${filterOpen ? " on" : ""}`}
          onClick={() => setFilterOpen((o) => !o)}
        >
          <Icon name="filter" size={14} /> Filter
        </button>
        {(teamsQ.data?.teams ?? []).length > 0 && (
          <select
            className="select"
            style={{ width: 150, height: 32 }}
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            aria-label="Filter by team"
          >
            <option value="">All teams</option>
            {(teamsQ.data?.teams ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        )}
        <span className="spacer" style={{ flex: 1 }} />
        <div className="input-search" style={{ width: 220 }}>
          <Icon name="search" size={14} />
          <input
            type="search"
            className="input"
            placeholder="Search tasks…"
            aria-label="Search tasks"
            value={search}
            onChange={(e) => updateSearch(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() =>
            void navigate({
              to: "/projects/$projectId/tasks/new",
              params: { projectId },
            })
          }
        >
          <Icon name="plus" size={14} /> Add task
        </button>
      </div>

      {showNew && (
        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "var(--r-lg)",
            padding: 16,
            margin: "14px 32px",
          }}
        >
          <TaskForm
            projectId={projectId}
            onCreated={() => {
              setShowNew(false);
              void qc.invalidateQueries({
                queryKey: queryKeys.tasksFor(projectId),
              });
            }}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowNew(false)}
            style={{ marginTop: 8 }}
          >
            Cancel
          </Button>
        </div>
      )}

      {groupBy === "status" ? (
        <KanbanBoard
          projectId={projectId}
          tasks={filteredTasks}
          users={usersMap}
          groupBy="status"
          onAddTaskInColumn={(status) => {
            void navigate({
              to: "/projects/$projectId/tasks/new",
              params: { projectId },
              search: { status },
            });
          }}
        />
      ) : (
        <SwimlaneBoard
          projectId={projectId}
          tasks={filteredTasks}
          users={usersMap}
          laneBy={groupBy}
          epicNames={
            new Map((epics.data?.epics ?? []).map((e) => [e.id, e.name]))
          }
          milestoneNames={
            new Map(
              (milestonesQ.data?.milestones ?? []).map((m) => [m.id, m.name]),
            )
          }
          onAddTaskInColumn={(status) => {
            void navigate({
              to: "/projects/$projectId/tasks/new",
              params: { projectId },
              search: { status },
            });
          }}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/projects/$projectId/")({
  // `q` (search text) is shared with the List view so the filter survives a
  // view switch and a reload.
  validateSearch: (s: Record<string, unknown>): { q?: string } => ({
    q: typeof s.q === "string" && s.q ? s.q : undefined,
  }),
  component: Board,
});
